import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { FeatureFlagGuard } from '@/components/guards';
import Journeys from '@/pages/journeys';
import NotFoundPage from '@/pages/not-found.page';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import { useGoalOptional } from '@/services/db/goal/queries';
import { getBreadcrumbsStore } from '@/services/breadcrumbs';
import type { BreadcrumbItem } from '@/services/breadcrumbs';
import { useLocation } from '@/services/router/compat';
import { dataIndexListPath, dataIndexPath } from '@/pages/workspaces/data-index/urls';
import { AppBundle } from '@/utils/app-bundles';

const CONVERSION_CRUMB_IDS = ['data-index', 'data-index-conversions', 'data-index-conversion'] as const;

function isConversionBreadcrumbId(id: string) {
  return id === CONVERSION_CRUMB_IDS[0] || id === CONVERSION_CRUMB_IDS[1] || id === CONVERSION_CRUMB_IDS[2];
}

function conversionBreadcrumbs(workspaceId: string, goalName: string): BreadcrumbItem[] {
  return [
    { id: CONVERSION_CRUMB_IDS[0], text: 'Data index', href: dataIndexPath(workspaceId) },
    {
      id: CONVERSION_CRUMB_IDS[1],
      text: 'Conversions',
      href: dataIndexListPath(workspaceId, 'conversions'),
    },
    { id: CONVERSION_CRUMB_IDS[2], text: goalName, href: '#', isTruncated: true },
  ];
}

function replaceJourneysBreadcrumbs(breadcrumbs: ReadonlyArray<BreadcrumbItem>) {
  getBreadcrumbsStore(AppBundle.Journeys).set([...breadcrumbs]);
}

function rollbackConversionBreadcrumbs() {
  const store = getBreadcrumbsStore(AppBundle.Journeys);
  store.set(store.get().filter((breadcrumb) => !isConversionBreadcrumbId(breadcrumb.id)));
}

type GoalRouteLocation = {
  workspaceId: string;
  goalId: string;
};

type ConversionBreadcrumbsProps = {
  children: ReactNode;
};

function goalRouteLocation(pathname: string): GoalRouteLocation | null {
  const match = pathname.match(/^\/workspaces\/([^/]+)\/journeys\/goals\/([^/?#]+)/);
  if (match == null) return null;

  try {
    return { workspaceId: match[1], goalId: decodeURIComponent(match[2]) };
  } catch {
    return { workspaceId: match[1], goalId: match[2] };
  }
}

function ConversionBreadcrumbs({ children }: ConversionBreadcrumbsProps) {
  const location = useLocation();
  const journeysBreadcrumbs = useStore(getBreadcrumbsStore(AppBundle.Journeys));
  const route = goalRouteLocation(location.pathname);
  const isConversionEntry = new URLSearchParams(location.searchStr).get('from') === 'conversions';
  const numericWorkspaceId = route == null ? 0 : Number(route.workspaceId);
  const goal = useGoalOptional({
    workspaceId: numericWorkspaceId,
    id: route?.goalId ?? '',
    enabled: isConversionEntry,
  });
  const goalName = goal.data?.name;

  useEffect(() => {
    if (!isConversionEntry || route == null || goalName == null) return;

    replaceJourneysBreadcrumbs(conversionBreadcrumbs(route.workspaceId, goalName));

    return () => {
      rollbackConversionBreadcrumbs();
    };
  }, [goalName, isConversionEntry, location.pathname, location.searchStr, route?.goalId, route?.workspaceId]);

  useEffect(() => {
    if (!isConversionEntry || route == null || goalName == null) return;

    const conversionTrail = journeysBreadcrumbs.filter((breadcrumb) => isConversionBreadcrumbId(breadcrumb.id));
    if (conversionTrail.length > 0 && conversionTrail.length !== journeysBreadcrumbs.length) {
      replaceJourneysBreadcrumbs(conversionTrail);
    }
  }, [goalName, isConversionEntry, journeysBreadcrumbs, route?.goalId, route?.workspaceId]);

  return <>{children}</>;
}

/**
 * Serves the `/journeys/goals` subtree (FLO-4772).
 *
 * Goals have no Hydra page — the pages live in the embedded Journeys app — so
 * flag-on this renders the embedded host and behaves exactly as the
 * `/journeys/$` splat did before these routes existed. Flag-off it renders
 * Hydra's own not-found instead: the goals surfaces are being hidden, and a
 * hidden page must not still answer to a bookmark or a hand-typed URL.
 * Masquerading staff are the exception: they need the embedded goal detail
 * pages to inspect conversion rows before the customer rollout flags are on.
 *
 * Gating here rather than through `route-ownership`'s `flag` field on purpose:
 * that field means "Hydra owns this route while the flag is ON" (rollout
 * semantics), which is the opposite of what goals needs, and ownership is
 * advisory metadata — it never renders anything. The not-found has to come
 * from a route component, and `FeatureFlagGuard` subscribes to the account so
 * it re-evaluates when the flags land rather than caching the pre-auth default.
 *
 * `route-ownership` still gets a matching entry (see `unlessFlag` there): with
 * ownership left saying `embedded` while the flag is off, an intra-Journeys
 * client-side navigation into a goals URL would take the embedded-history
 * fast path in `installEmbeddedAppHistorySync`, skip the TanStack route load
 * entirely, and this guard would never run.
 */
export function GoalsRoute() {
  const account = useStore(accountStore);

  if (account?.is_masq === true) {
    return (
      <ConversionBreadcrumbs>
        <Journeys />
      </ConversionBreadcrumbs>
    );
  }

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.ShowGoals} fallback={NotFoundPage}>
      <ConversionBreadcrumbs>
        <Journeys />
      </ConversionBreadcrumbs>
    </FeatureFlagGuard>
  );
}
